import { Plus, Trash2 } from 'lucide-react'
import { Button, IconButton, Input, Select } from '@/components/ui'
import type { ConditionNode, TemplateVariable, VariableType } from './types.ts'

// Operators offered per variable type. Values match the backend condition.ts evaluator.
const OPS_BY_TYPE: Record<VariableType, { value: string; label: string; noValue?: boolean }[]> = {
  number: [
    { value: 'eq', label: '=' }, { value: 'neq', label: '≠' },
    { value: 'lt', label: '<' }, { value: 'lte', label: '≤' },
    { value: 'gt', label: '>' }, { value: 'gte', label: '≥' },
  ],
  date: [
    { value: 'eq', label: 'am' }, { value: 'lt', label: 'vor' }, { value: 'gt', label: 'nach' },
    { value: 'lte', label: 'bis' }, { value: 'gte', label: 'ab' },
  ],
  datetime: [
    { value: 'eq', label: 'am' }, { value: 'lt', label: 'vor' }, { value: 'gt', label: 'nach' },
    { value: 'lte', label: 'bis' }, { value: 'gte', label: 'ab' },
  ],
  text: [
    { value: 'eq', label: 'ist' }, { value: 'neq', label: 'ist nicht' },
    { value: 'contains', label: 'enthält' },
    { value: 'is_set', label: 'ist gesetzt', noValue: true },
    { value: 'is_empty', label: 'ist leer', noValue: true },
  ],
  select: [
    { value: 'eq', label: 'ist' }, { value: 'neq', label: 'ist nicht' },
    { value: 'in', label: 'ist eine von' },
  ],
  bool: [
    { value: 'is_true', label: 'ist wahr', noValue: true },
    { value: 'is_false', label: 'ist falsch', noValue: true },
  ],
  entity: [
    { value: 'eq', label: 'ist' }, { value: 'neq', label: 'ist nicht' },
    { value: 'is_set', label: 'ist gesetzt', noValue: true },
    { value: 'is_empty', label: 'ist leer', noValue: true },
  ],
}

interface Comparison { field: string; op: string; value?: unknown }

/** Flattens the stored AST into combinator + comparison rows for editing. */
function toRows(node: ConditionNode | null): { combinator: 'all' | 'any'; rows: Comparison[] } {
  if (!node) return { combinator: 'all', rows: [] }
  if ('all' in node && Array.isArray(node.all)) {
    return { combinator: 'all', rows: node.all.filter(isComparison) as Comparison[] }
  }
  if ('any' in node && Array.isArray(node.any)) {
    return { combinator: 'any', rows: node.any.filter(isComparison) as Comparison[] }
  }
  if (isComparison(node)) return { combinator: 'all', rows: [node as Comparison] }
  return { combinator: 'all', rows: [] }
}

function isComparison(n: ConditionNode): boolean {
  return typeof n === 'object' && n !== null && 'field' in n && 'op' in n
}

/** Rebuilds the AST from combinator + rows; empty → null (unconditional). */
function toNode(combinator: 'all' | 'any', rows: Comparison[]): ConditionNode | null {
  const valid = rows.filter((r) => r.field && r.op)
  if (valid.length === 0) return null
  if (valid.length === 1) return valid[0] as ConditionNode
  return { [combinator]: valid } as ConditionNode
}

interface RuleBuilderProps {
  variables: TemplateVariable[]
  value: ConditionNode | null
  onChange: (next: ConditionNode | null) => void
}

export function RuleBuilder({ variables, value, onChange }: RuleBuilderProps) {
  const { combinator, rows } = toRows(value)

  const varByKey = new Map(variables.map((v) => [v.var_key, v]))
  const fieldOptions = [
    { value: '', label: 'Variable wählen …' },
    ...variables.map((v) => ({ value: v.var_key, label: v.label })),
  ]

  function emit(nextCombinator: 'all' | 'any', nextRows: Comparison[]) {
    onChange(toNode(nextCombinator, nextRows))
  }

  function updateRow(idx: number, patch: Partial<Comparison>) {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    emit(combinator, next)
  }

  function addRow() {
    emit(combinator, [...rows, { field: '', op: 'eq', value: '' }])
  }

  function removeRow(idx: number) {
    emit(combinator, rows.filter((_, i) => i !== idx))
  }

  if (variables.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        Noch keine Variablen definiert. Bedingungen benötigen Variablen — lege sie über
        „Variablen" oben an.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {rows.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          <span>Treffer wenn</span>
          <Select
            value={combinator}
            onValueChange={(v) => emit(v as 'all' | 'any', rows)}
            options={[
              { value: 'all', label: 'alle Bedingungen' },
              { value: 'any', label: 'mindestens eine' },
            ]}
          />
          <span>zutreffen</span>
        </div>
      )}

      {rows.map((row, idx) => {
        const variable = varByKey.get(row.field)
        const ops = variable ? OPS_BY_TYPE[variable.var_type] : OPS_BY_TYPE.text
        const opMeta = ops.find((o) => o.value === row.op)
        const choices =
          variable?.var_type === 'select'
            ? ((variable.options?.['choices'] as { value: string; label: string }[] | undefined) ?? [])
            : []
        return (
          <div key={idx} className="flex items-start gap-1.5">
            <div className="grid grid-cols-1 gap-1.5 flex-1">
              <Select
                value={row.field}
                onValueChange={(v) => updateRow(idx, { field: v, op: 'eq', value: '' })}
                options={fieldOptions}
              />
              <div className="flex gap-1.5">
                <Select
                  value={row.op}
                  onValueChange={(v) => updateRow(idx, { op: v })}
                  options={ops.map((o) => ({ value: o.value, label: o.label }))}
                />
                {!opMeta?.noValue &&
                  (choices.length > 0 ? (
                    <Select
                      value={String(row.value ?? '')}
                      onValueChange={(v) => updateRow(idx, { value: v })}
                      options={[{ value: '', label: '—' }, ...choices]}
                    />
                  ) : (
                    <Input
                      type={variable?.var_type === 'number' ? 'number' : variable?.var_type === 'date' ? 'date' : 'text'}
                      value={String(row.value ?? '')}
                      onChange={(e) =>
                        updateRow(idx, {
                          value: variable?.var_type === 'number' ? Number(e.target.value) : e.target.value,
                        })
                      }
                      placeholder="Wert …"
                    />
                  ))}
              </div>
            </div>
            <IconButton
              icon={<Trash2 />}
              aria-label="Bedingung entfernen"
              variant="ghost"
              size="sm"
              onClick={() => removeRow(idx)}
            />
          </div>
        )
      })}

      <Button variant="ghost" size="xs" leadingIcon={<Plus className="w-3.5 h-3.5" />} onClick={addRow}>
        Bedingung hinzufügen
      </Button>
    </div>
  )
}
